import axios from "axios";
import { API_BASE_URL } from "@/lib/apiConfig";
import {
  getAccessToken,
  getAuthTokens,
  getRefreshToken,
  setAuthTokens,
} from "@/lib/authTokens";
import { PANEL_API_BASE } from "@/lib/panelConfig";

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;
  const { data } = await axios.post<{ access: string }>(`${PANEL_API_BASE}/auth/refresh/`, {
    refresh,
  });
  const { refresh: storedRefresh } = getAuthTokens();
  setAuthTokens(data.access, storedRefresh);
  return data.access;
}

api.interceptors.request.use((config) => {
  const access = getAccessToken();
  if (access) {
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && getRefreshToken() && original && !original._retry) {
      original._retry = true;
      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
      const newAccess = await refreshPromise;
      if (newAccess) {
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
      }
    }
    return Promise.reject(error);
  },
);

export function setAccessToken(token: string | null) {
  const { refresh } = getAuthTokens();
  if (token && refresh) {
    setAuthTokens(token, refresh);
  }
}

export function getApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { detail?: string } | undefined;
    if (typeof data?.detail === "string") return data.detail;
    if (error.message) return error.message;
  }
  return "Ocorreu um erro inesperado. Tente novamente.";
}

export function isAxiosNotFound(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

export function isAxiosServerError(error: unknown): boolean {
  return axios.isAxiosError(error) && (error.response?.status ?? 0) >= 500;
}
